/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import * as React from "react";
import {
  KpiTimePeriod,
  OrgUnitKpiDataQuery,
  useOrgUnitKpiDataQuery,
} from "~/generated/graphql";
import { getKpiFetchConfig } from "~/components/KPI/utils";
import { KpiType } from "~/constants";

type Props = {
  orgUnitId?: string;
  period?: KpiTimePeriod;
  kpiType: KpiType;
};

export function useOrgUnitKpi({ period, orgUnitId, kpiType }: Props) {
  const { data, loading, error } = useOrgUnitKpiDataQuery({
    variables: {
      id: orgUnitId ?? "",
      period: period ?? KpiTimePeriod.ThreeMonths,
      ...getKpiFetchConfig(kpiType),
    },
    skip: !orgUnitId,
  });

  const mappedAdditionalData = React.useMemo(
    () => mapOrgUnitKpi(kpiType, data),
    [data, kpiType]
  );

  return {
    data: mappedAdditionalData,
    loading,
    error,
  };
}

function mapOrgUnitKpi(kpiType: KpiType, data?: OrgUnitKpiDataQuery) {
  if (!data) {
    return;
  }

  const kpiDetails = getOrgUnitKpiDetails(kpiType, data);

  return {
    timePoints: kpiDetails?.details.metrics?.values ?? [],
  };
}

function getOrgUnitKpiDetails(kpiType: KpiType, data?: OrgUnitKpiDataQuery) {
  const kpis = data?.orgUnitV2?.kpis;

  switch (kpiType) {
    case KpiType.NON_PROD_HOURS:
      return kpis?.nonProdHours;
    case KpiType.OVERALL_WASTE:
      return kpis?.overallWaste;
    default:
      return;
  }
}
